import { useMutation } from '@apollo/client';
import { RecalculateAppetitesDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useRecalculateAppetitesTRPC } from './useRecalculateAppetitesTRPC';

/**
 * Outcome of a recalculate-appetites call. `errorCode` is a *successful* gating
 * outcome (the cascade was skipped because the org isn't configured for it),
 * surfaced so the caller can show a specific message. `null` means the cascade
 * ran. The string values match across the Apollo and tRPC paths — both the
 * GraphQL `Recalculate_Appetites_Error_Code_Enum` and the tRPC response use
 * `'cascade_not_configured'` / `'cascade_not_enabled'`.
 */
export interface RecalculateAppetitesResult {
  errorCode: string | null;
}

/**
 * Flag-gated wrapper for recalculateAppetites (RSP-6486). On the `trpc` flag it
 * routes through the tRPC async-request path (server-side cascade compute);
 * otherwise it falls back to the existing Apollo mutation. The Apollo path is
 * retained until the flag is fully rolled out and the GraphQL action is removed
 * (do NOT hard-switch — see packages/web/CLAUDE.md). Both paths return the same
 * `{ errorCode }` shape so the consumer's branching is path-agnostic.
 */
export const useRecalculateAppetites = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [recalculateAppetitesGraphQL, graphqlState] = useMutation(
    RecalculateAppetitesDocument
  );

  const trpcMutation = useRecalculateAppetitesTRPC();

  const recalculateAppetites =
    async (): Promise<RecalculateAppetitesResult> => {
      if (trpcEnabled) {
        return trpcMutation.recalculateAppetites();
      }

      const result = await recalculateAppetitesGraphQL();

      return {
        errorCode: result.data?.recalculateAppetites?.errorCode ?? null,
      };
    };

  if (trpcEnabled) {
    return {
      recalculateAppetites,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    recalculateAppetites,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
